import React, { useState, useRef, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { 
  Video, 
  Upload, 
  Camera,
  Facebook, 
  Instagram, 
  Linkedin, 
  Send, 
  Loader2,
  CheckCircle2,
  AlertCircle,
  Clock,
  RefreshCw,
  Plus,
  Eye,
  Trash2,
  Edit,
  Square,
  Play,
  Pause,
  Palette
} from 'lucide-react';
import logoIcon from "@/assets/regard-de-nature-logo.png";

// Hashtags prédéfinis naturopathie
const NATUROPATHY_HASHTAGS = [
  '#naturopathie', '#bienetre', '#sante', '#naturel', '#iridologie',
  '#phytotherapie', '#aromatherapie', '#gemmotherapie', '#hygienevitale',
  '#santeholistique', '#medecinenatturelle', '#fleursdebach', '#equilibre',
  '#vitalite', '#terrainbiologique', '#preventionsante', '#naturopathe',
  '#bienetrenaturel', '#conseilssante', '#hygienedevie', '#reels', '#reelsinstagram'
];

// Branding options types
// Available fonts for text overlay (Cloudinary-compatible - no spaces in names!)
// See: https://cloudinary.com/documentation/image_transformations#text_overlays
const AVAILABLE_FONTS = [
  { value: 'Montserrat', label: 'Montserrat' },
  { value: 'Arial', label: 'Arial' },
  { value: 'Georgia', label: 'Georgia' },
  { value: 'Verdana', label: 'Verdana' },
  { value: 'Roboto', label: 'Roboto' },
  { value: 'Helvetica', label: 'Helvetica' },
  { value: 'Impact', label: 'Impact' },
  { value: 'Tahoma', label: 'Tahoma' },
] as const;

// Available text colors
const AVAILABLE_COLORS = [
  { value: 'white', label: 'Blanc', hex: '#FFFFFF' },
  { value: 'black', label: 'Noir', hex: '#000000' },
  { value: 'yellow', label: 'Jaune', hex: '#FFFF00' },
  { value: 'gold', label: 'Or', hex: '#CCA727' },
  { value: 'green', label: 'Vert', hex: '#506842' },
  { value: 'red', label: 'Rouge', hex: '#FF0000' },
  { value: 'blue', label: 'Bleu', hex: '#0066CC' },
  { value: 'orange', label: 'Orange', hex: '#FF6600' },
] as const;

interface VideoBranding {
  apply_branding: boolean;
  logo: {
    visible: boolean;
    position: 'top_left' | 'top_right' | 'bottom_left' | 'bottom_right';
    size_px: number;
    margin_px: number;
  };
  visual_title: {
    enabled: boolean;
    text: string | null;
    position: 'top_left' | 'top_center' | 'top_right' | 'bottom_left' | 'bottom_center' | 'bottom_right';
    size_px: number;
    font: string;
    color: string;
  };
}

const DEFAULT_BRANDING: VideoBranding = {
  apply_branding: false,
  logo: {
    visible: true,
    position: 'top_left',
    size_px: 140,
    margin_px: 40,
  },
  visual_title: {
    enabled: false,
    text: null,
    position: 'bottom_center',
    size_px: 48,
    font: 'Montserrat',
    color: 'white',
  },
};

interface VideoDraft {
  id: string;
  title: string;
  status: 'pending' | 'ready' | 'published' | 'error';
  channels: {
    instagram_reel: boolean;
    facebook_reel: boolean;
    linkedin_reel: boolean;
    tiktok: boolean;
  };
  video_url: string | null;
  poster_url: string | null;
  duration_seconds: number | null;
  caption: string;
  hashtags: string;
  branding: VideoBranding;
  published_at: string | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
}

interface GlobalSettings {
  make_webhook_social_url: string | null;
  social_publish_enabled: boolean;
  social_publish_mode: 'test' | 'production';
}

interface VideoReelsDraftsProps {
  settings: GlobalSettings | null;
}

function parseEdgeFunctionError(err: any): { code?: string; message: string } {
  const generic = typeof err?.message === 'string' ? err.message : 'Erreur inconnue';
  const body = err?.context?.body;
  if (typeof body === 'string') {
    try {
      const parsed = JSON.parse(body);
      return { code: parsed?.error, message: parsed?.message || generic };
    } catch {
      return { message: body || generic };
    }
  }
  return { message: generic };
}

export function VideoReelsDrafts({ settings }: VideoReelsDraftsProps) {
  const [drafts, setDrafts] = useState<VideoDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [brandingInProgress, setBrandingInProgress] = useState(false);
  
  // Modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [currentDraft, setCurrentDraft] = useState<Partial<VideoDraft> | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  
  // Video recording
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [uploading, setUploading] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const videoPreviewRef = useRef<HTMLVideoElement>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  
  const { toast } = useToast();

  // Load drafts on mount
  React.useEffect(() => {
    loadDrafts();
  }, []);

  const loadDrafts = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Load video drafts from social_publication_drafts where entity_type = 'video_reel'
      const { data, error } = await supabase
        .from('social_publication_drafts')
        .select('*')
        .eq('user_id', user.id)
        .eq('entity_type', 'video_reel')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Map to local type
      const mappedDrafts: VideoDraft[] = (data || []).map((d: any) => {
        const channels = d.channels as any || {};
        const media = d.media as any || {};
        const copy = d.copy as any || {};
        const storedBranding = media.branding || {};
        
        return {
          id: d.id,
          title: d.title,
          status: d.status as VideoDraft['status'],
          channels: {
            instagram_reel: channels.instagram_reel || false,
            facebook_reel: channels.facebook_reel || false,
            linkedin_reel: channels.linkedin_reel || false,
            tiktok: channels.tiktok || false,
          },
          video_url: media.reel?.video_url || null,
          poster_url: media.reel?.poster_url || null,
          duration_seconds: media.reel?.duration_seconds || null,
          caption: copy.reel?.caption || copy.default || '',
          hashtags: copy.instagram?.hashtags || '',
          branding: {
            apply_branding: storedBranding.apply_branding ?? false,
            logo: {
              visible: storedBranding.logo?.visible ?? true,
              position: storedBranding.logo?.position || 'top_left',
              size_px: storedBranding.logo?.size_px || 140,
              margin_px: storedBranding.logo?.margin_px || 40,
            },
            visual_title: {
              enabled: storedBranding.visual_title?.enabled ?? false,
              text: storedBranding.visual_title?.text || d.visual_title || null,
              position: storedBranding.visual_title?.position || 'bottom_center',
              size_px: storedBranding.visual_title?.size_px || 48,
              font: storedBranding.visual_title?.font || 'Montserrat',
              color: storedBranding.visual_title?.color || 'white',
            },
          },
          published_at: d.published_at,
          last_error: d.last_error,
          created_at: d.created_at,
          updated_at: d.updated_at,
        };
      });

      setDrafts(mappedDrafts);
    } catch (error: any) {
      console.error('Error loading video drafts:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les brouillons vidéo",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setCurrentDraft({
      title: '',
      status: 'pending',
      channels: {
        instagram_reel: true,
        facebook_reel: false,
        linkedin_reel: false,
        tiktok: false,
      },
      video_url: null,
      poster_url: null,
      duration_seconds: null,
      caption: '',
      hashtags: '',
      branding: { ...DEFAULT_BRANDING },
    });
    setRecordedBlob(null);
    setIsCreating(true);
    setEditModalOpen(true);
  };

  const openEditModal = (draft: VideoDraft) => {
    setCurrentDraft(draft);
    setRecordedBlob(null);
    setIsCreating(false);
    setEditModalOpen(true);
  };

  // Start camera for recording
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1080 },
          height: { ideal: 1920 },
          facingMode: 'user',
          aspectRatio: { ideal: 9 / 16 },
        },
        audio: true,
      });
      
      streamRef.current = stream;
      
      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = stream;
        videoPreviewRef.current.play();
      }
      
      toast({ title: "Caméra activée", description: "Prêt à enregistrer" });
    } catch (error: any) {
      console.error('Camera error:', error);
      toast({
        title: "Erreur caméra",
        description: error.message || "Impossible d'accéder à la caméra",
        variant: "destructive",
      });
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoPreviewRef.current) {
      videoPreviewRef.current.srcObject = null;
    }
  };

  // Determine best supported video format for recording
  const getSupportedMimeType = (): string => {
    const mimeTypes = [
      'video/mp4;codecs=avc1,mp4a.40.2',
      'video/mp4;codecs=avc1',
      'video/mp4',
      'video/webm;codecs=h264',
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp9',
      'video/webm;codecs=vp8,opus',
      'video/webm;codecs=vp8',
      'video/webm',
    ];
    
    for (const mimeType of mimeTypes) {
      if (MediaRecorder.isTypeSupported(mimeType)) {
        console.log('Using MIME type for recording:', mimeType);
        return mimeType;
      }
    }
    
    return 'video/webm';
  };

  const startRecording = () => {
    if (!streamRef.current) {
      toast({ title: "Caméra non active", variant: "destructive" });
      return;
    }

    chunksRef.current = [];
    const mimeType = getSupportedMimeType();
    const isMP4 = mimeType.includes('mp4');
    
    const mediaRecorder = new MediaRecorder(streamRef.current, {
      mimeType,
      videoBitsPerSecond: 5000000,
    });

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };

    mediaRecorder.onstop = () => {
      const recordedType = isMP4 ? 'video/mp4' : 'video/webm';
      const blob = new Blob(chunksRef.current, { type: recordedType });
      setRecordedBlob(blob);
      stopCamera();
      
      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = null;
        videoPreviewRef.current.src = URL.createObjectURL(blob);
        videoPreviewRef.current.controls = true;
      }
      
      if (!isMP4) {
        toast({ 
          title: "Vidéo enregistrée (WebM)", 
          description: "Cloudinary convertira automatiquement en MP4",
        });
      }
    };

    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.start(1000);
    setIsRecording(true);
    toast({ 
      title: "Enregistrement en cours...", 
      description: `Format: ${isMP4 ? 'MP4' : 'WebM'} - Cliquez sur Stop pour terminer`
    });
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // Handle file upload
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      toast({
        title: "Format invalide",
        description: "Veuillez sélectionner un fichier vidéo",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 100 * 1024 * 1024) {
      toast({
        title: "Fichier trop volumineux",
        description: "La vidéo ne doit pas dépasser 100 Mo",
        variant: "destructive",
      });
      return;
    }

    setRecordedBlob(file);
    
    if (videoPreviewRef.current) {
      videoPreviewRef.current.srcObject = null;
      videoPreviewRef.current.src = URL.createObjectURL(file);
      videoPreviewRef.current.controls = true;
    }

    toast({ title: "Vidéo chargée", description: file.name });
  };

  const getActualMimeType = (blob: Blob): string => {
    if (blob.type && blob.type !== '') {
      return blob.type;
    }
    return 'video/mp4';
  };

  // Upload video to Supabase
  const uploadVideo = async (blob: Blob, draftId: string): Promise<{ url: string; duration: number; mimeType: string } | null> => {
    setUploading(true);
    try {
      const timestamp = Date.now();
      const mimeType = getActualMimeType(blob);
      const extension = mimeType.includes('webm') ? 'webm' : 'mp4';
      const fileName = `social-videos/${draftId}/reel-${timestamp}.${extension}`;
      
      console.log('Uploading video:', { mimeType, extension, size: blob.size });
      
      const { error: uploadError } = await supabase.storage
        .from('assets')
        .upload(fileName, blob, {
          contentType: mimeType,
          upsert: true,
        });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('assets')
        .getPublicUrl(fileName);

      let duration = 0;
      try {
        const video = document.createElement('video');
        video.src = URL.createObjectURL(blob);
        await new Promise<void>((resolve) => {
          video.onloadedmetadata = () => {
            duration = Math.round(video.duration);
            resolve();
          };
          video.onerror = () => resolve();
        });
      } catch (e) {
        console.warn('Could not get video duration:', e);
      }

      return { url: urlData?.publicUrl || '', duration, mimeType };
    } catch (error: any) {
      console.error('Video upload error:', error);
      toast({
        title: "Erreur upload",
        description: error.message,
        variant: "destructive",
      });
      return null;
    } finally {
      setUploading(false);
    }
  };

  // Apply Cloudinary branding
  const applyCloudinaryBranding = async (videoUrl: string, branding: VideoBranding): Promise<string> => {
    if (!branding.apply_branding) {
      return videoUrl;
    }
    
    setBrandingInProgress(true);
    try {
      console.log('[Branding] Calling Cloudinary branding edge function...');
      
      const { data, error } = await supabase.functions.invoke('cloudinary-video-branding', {
        body: {
          action: 'brand_video',
          video_url: videoUrl,
          branding: branding,
        },
      });
      
      if (error) {
        console.error('[Branding] Edge function error:', error);
        toast({
          title: "Branding échoué",
          description: "Utilisation de la vidéo originale",
          variant: "destructive",
        });
        return videoUrl;
      }
      
      if (data?.branded_url) {
        console.log('[Branding] Success, branded URL:', data.branded_url);
        toast({ title: "Branding appliqué ✓" });
        return data.branded_url;
      }
      
      return videoUrl;
    } catch (err: any) {
      console.error('[Branding] Error:', err);
      toast({
        title: "Erreur branding",
        description: err.message || "Utilisation de la vidéo originale",
        variant: "destructive",
      });
      return videoUrl;
    } finally {
      setBrandingInProgress(false);
    }
  };

  const saveDraft = async () => {
    if (!currentDraft || !currentDraft.title) {
      toast({
        title: "Titre requis",
        description: "Veuillez entrer un titre pour ce brouillon",
        variant: "destructive",
      });
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    try {
      let video_url = currentDraft.video_url;
      let duration_seconds = currentDraft.duration_seconds;
      let videoMimeType = 'video/mp4';

      // Upload video if new blob exists
      if (recordedBlob) {
        const tempId = currentDraft.id || crypto.randomUUID();
        const result = await uploadVideo(recordedBlob, tempId);
        if (result) {
          video_url = result.url;
          duration_seconds = result.duration;
          videoMimeType = result.mimeType;
        }
      }

      const branding = currentDraft.branding || DEFAULT_BRANDING;
      
      // Convert branding to JSON-compatible object
      const brandingJson = {
        apply_branding: branding.apply_branding,
        logo: {
          visible: branding.logo.visible,
          position: branding.logo.position,
          size_px: branding.logo.size_px,
          margin_px: branding.logo.margin_px,
        },
        visual_title: {
          enabled: branding.visual_title.enabled,
          text: branding.visual_title.text,
          position: branding.visual_title.position,
          size_px: branding.visual_title.size_px,
        },
      };

      const draftData = {
        user_id: user.id,
        entity_type: 'video_reel' as const,
        entity_id: null,
        title: currentDraft.title,
        status: 'pending' as const,
        channels: {
          instagram_post: false,
          instagram_story: false,
          instagram_reel: currentDraft.channels?.instagram_reel || false,
          facebook_page: false,
          facebook_story: false,
          facebook_reel: currentDraft.channels?.facebook_reel || false,
          linkedin_page: false,
          linkedin_reel: currentDraft.channels?.linkedin_reel || false,
          tiktok: currentDraft.channels?.tiktok || false,
          newsletter: false,
        },
        copy: {
          default: currentDraft.caption || '',
          instagram: { 
            caption: currentDraft.caption || null, 
            hashtags: currentDraft.hashtags || null, 
            alt_text: null 
          },
          facebook: { message: currentDraft.caption || null },
          linkedin: { text: null },
          story: { text: null, cta: { label: null, url: null } },
          reel: { caption: currentDraft.caption || null },
          email: { subject: null, preheader: null },
        },
        media: {
          mode: 'video_story_reel',
          cover: { url: null, type: null, width: null, height: null },
          carousel: { items: [] },
          video: { 
            url: video_url, 
            type: videoMimeType, 
            duration_seconds, 
            poster_url: currentDraft.poster_url || null,
            width: 1080,
            height: 1920,
          },
          story: { image_url: null, video_url, type: videoMimeType, width: 1080, height: 1920 },
          reel: { 
            video_url, 
            type: videoMimeType, 
            duration_seconds, 
            poster_url: currentDraft.poster_url || null,
            width: 1080, 
            height: 1920,
          },
          branding: brandingJson,
        },
        render_mode: 'legacy_image' as const,
        share_url: null,
        schedule: { publish_at: null, timezone: 'Europe/Paris' },
        visual_title: branding.visual_title.text,
        show_logo: branding.logo.visible,
        show_site_name: true,
      };

      if (isCreating) {
        const { data, error } = await supabase
          .from('social_publication_drafts')
          .insert(draftData)
          .select()
          .single();

        if (error) throw error;
        
        const channelsData = data.channels as any || {};
        const newDraft: VideoDraft = {
          id: data.id,
          title: data.title,
          status: data.status as VideoDraft['status'],
          channels: {
            instagram_reel: channelsData.instagram_reel || false,
            facebook_reel: channelsData.facebook_reel || false,
            linkedin_reel: channelsData.linkedin_reel || false,
            tiktok: channelsData.tiktok || false,
          },
          video_url,
          poster_url: currentDraft.poster_url || null,
          duration_seconds,
          caption: currentDraft.caption || '',
          hashtags: currentDraft.hashtags || '',
          branding: branding,
          published_at: null,
          last_error: null,
          created_at: data.created_at,
          updated_at: data.updated_at,
        };
        setDrafts([newDraft, ...drafts]);
        toast({ title: "Brouillon vidéo créé" });
      } else {
        const { error } = await supabase
          .from('social_publication_drafts')
          .update({
            title: currentDraft.title,
            channels: draftData.channels,
            copy: draftData.copy,
            media: draftData.media,
            visual_title: branding.visual_title.text,
            show_logo: branding.logo.visible,
            show_site_name: true,
            status: 'ready',
          })
          .eq('id', currentDraft.id!);

        if (error) throw error;
        
        setDrafts(drafts.map(d => 
          d.id === currentDraft.id 
            ? { 
                ...d, 
                ...currentDraft, 
                video_url,
                duration_seconds,
                branding,
                status: 'ready' as const,
              } 
            : d
        ));
        toast({ title: "Brouillon mis à jour" });
      }

      setEditModalOpen(false);
      setCurrentDraft(null);
      setRecordedBlob(null);
      stopCamera();
    } catch (error: any) {
      console.error('Save error:', error);
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
    }
  };

  const deleteDraft = async (id: string) => {
    if (!confirm('Supprimer ce brouillon vidéo ?')) return;

    const { error } = await supabase
      .from('social_publication_drafts')
      .delete()
      .eq('id', id);

    if (error) {
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
    } else {
      setDrafts(drafts.filter(d => d.id !== id));
      toast({ title: "Brouillon supprimé" });
    }
  };

  const handlePublish = async (draft: VideoDraft) => {
    if (draft.published_at) {
      toast({
        title: "Déjà publié",
        description: `Ce brouillon a déjà été publié le ${new Date(draft.published_at).toLocaleString('fr-FR')}`,
      });
      return;
    }

    if (settings?.social_publish_mode !== 'production') {
      toast({
        title: "Mode Production requis",
        description: "La publication n'est autorisée qu'en mode Production",
        variant: "destructive",
      });
      return;
    }

    if (!settings?.make_webhook_social_url) {
      toast({
        title: "Configuration manquante",
        description: "Configurez le webhook Make.com dans Paramètres",
        variant: "destructive",
      });
      return;
    }

    if (!draft.video_url) {
      toast({
        title: "Vidéo manquante",
        description: "Veuillez d'abord uploader une vidéo",
        variant: "destructive",
      });
      return;
    }

    setPublishingId(draft.id);

    try {
      console.log('[VideoPublish] Starting publication for draft:', draft.id);
      console.log('[VideoPublish] Original video URL:', draft.video_url);
      console.log('[VideoPublish] Branding config:', draft.branding);

      // Apply Cloudinary branding if enabled
      let finalVideoUrl = draft.video_url;
      if (draft.branding.apply_branding) {
        toast({ title: "Application du branding Cloudinary...", description: "Veuillez patienter" });
        finalVideoUrl = await applyCloudinaryBranding(draft.video_url, draft.branding);
        console.log('[VideoPublish] Branded video URL:', finalVideoUrl);
      }

      const { data, error } = await supabase.functions.invoke('social-push-to-make', {
        body: { 
          action: 'publish_draft',
          draft_id: draft.id,
          rendered_media: {
            feed_url: null,
            story_url: null,
            video_url: finalVideoUrl,
          },
          is_video: true,
        },
      });

      if (error) throw error;

      setDrafts(drafts.map(d => 
        d.id === draft.id 
          ? { ...d, status: 'published' as const, published_at: new Date().toISOString() } 
          : d
      ));

      toast({
        title: "Publication réussie !",
        description: draft.branding.apply_branding 
          ? "La vidéo brandée a été envoyée vers Make.com"
          : "La vidéo a été envoyée vers Make.com",
      });
    } catch (error: any) {
      const parsed = parseEdgeFunctionError(error);
      console.error('[VideoPublish] Error:', parsed);

      await supabase
        .from('social_publication_drafts')
        .update({ status: 'error', last_error: parsed.message })
        .eq('id', draft.id);

      setDrafts(drafts.map(d =>
        d.id === draft.id
          ? { ...d, status: 'error' as const, last_error: parsed.message }
          : d
      ));

      toast({
        title: "Erreur de publication",
        description: parsed.message,
        variant: "destructive",
      });
    } finally {
      setPublishingId(null);
    }
  };

  const getStatusBadge = (status: VideoDraft['status']) => {
    switch (status) {
      case 'published':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="w-3 h-3 mr-1" />Publié</Badge>;
      case 'error':
        return <Badge className="bg-red-100 text-red-700"><AlertCircle className="w-3 h-3 mr-1" />Erreur</Badge>;
      case 'ready':
        return <Badge className="bg-blue-100 text-blue-700"><CheckCircle2 className="w-3 h-3 mr-1" />Prêt</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-600"><Clock className="w-3 h-3 mr-1" />En attente</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Video className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold">Vidéos Reels</h2>
          <Badge variant="secondary">{drafts.length}</Badge>
        </div>
        <Button onClick={openCreateModal}>
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle vidéo
        </Button>
      </div>

      {/* Drafts grid */}
      {drafts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Video className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground">Aucun brouillon vidéo</p>
            <p className="text-sm text-muted-foreground mt-1">
              Créez votre première vidéo Reel pour Instagram
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {drafts.map((draft) => (
            <Card key={draft.id} className={draft.status === 'error' ? 'border-red-200' : ''}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <CardTitle className="text-base line-clamp-1">{draft.title}</CardTitle>
                  {getStatusBadge(draft.status)}
                </div>
                {draft.last_error && (
                  <p className="text-xs text-red-600 mt-1">{draft.last_error}</p>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Video preview */}
                {draft.video_url && (
                  <div className="aspect-[9/16] max-h-32 bg-muted rounded overflow-hidden relative">
                    <video 
                      src={draft.video_url} 
                      className="w-full h-full object-cover"
                      preload="metadata"
                    />
                    {draft.branding.apply_branding && (
                      <Badge className="absolute top-1 right-1 bg-primary/80 text-xs">
                        <Palette className="w-3 h-3 mr-1" />
                        Branding
                      </Badge>
                    )}
                  </div>
                )}

                {/* Channels */}
                <div className="flex flex-wrap gap-1">
                  {draft.channels.instagram_reel && <Badge variant="secondary" className="text-xs"><Instagram className="w-3 h-3 mr-1" />Reel</Badge>}
                  {draft.channels.facebook_reel && <Badge variant="secondary" className="text-xs"><Facebook className="w-3 h-3 mr-1" />Reel</Badge>}
                  {draft.channels.linkedin_reel && <Badge variant="secondary" className="text-xs"><Linkedin className="w-3 h-3 mr-1" />Reel</Badge>}
                  {draft.channels.tiktok && <Badge variant="secondary" className="text-xs">TikTok</Badge>}
                </div>

                {/* Duration */}
                {draft.duration_seconds && (
                  <p className="text-xs text-muted-foreground">Durée: {draft.duration_seconds}s</p>
                )}

                {/* Caption preview */}
                {draft.caption && (
                  <p className="text-xs text-muted-foreground line-clamp-2">{draft.caption}</p>
                )}

                {/* Actions */}
                <div className="flex items-center justify-between pt-2 border-t">
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEditModal(draft)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => deleteDraft(draft.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handlePublish(draft)}
                    disabled={publishingId === draft.id || draft.status === 'published' || !!draft.published_at || !draft.video_url || brandingInProgress}
                  >
                    {publishingId === draft.id || brandingInProgress ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : draft.status === 'published' || draft.published_at ? (
                      <>
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Publié
                      </>
                    ) : draft.status === 'error' ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Relancer
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-1" />
                        Publier
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit/Create Modal */}
      <Dialog open={editModalOpen} onOpenChange={(open) => {
        if (!open) {
          stopCamera();
          setRecordedBlob(null);
        }
        setEditModalOpen(open);
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {isCreating ? 'Nouveau brouillon vidéo Reel' : 'Modifier le brouillon vidéo'}
            </DialogTitle>
            <DialogDescription>
              Format vertical 9:16 recommandé pour les Reels Instagram
            </DialogDescription>
          </DialogHeader>

          {currentDraft && (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Left: Form */}
              <div className="space-y-4">
                {/* Title */}
                <div className="space-y-2">
                  <Label>Titre interne</Label>
                  <Input
                    value={currentDraft.title || ''}
                    onChange={(e) => setCurrentDraft({ ...currentDraft, title: e.target.value })}
                    placeholder="Titre pour identifier ce reel"
                  />
                </div>

                <Separator />

                {/* Video upload/record */}
                <div className="space-y-3">
                  <Label>Vidéo (format 9:16)</Label>
                  
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById('video-upload')?.click()}
                      disabled={isRecording}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Importer
                    </Button>
                    <input
                      id="video-upload"
                      type="file"
                      accept="video/*"
                      className="hidden"
                      onChange={handleFileUpload}
                    />
                    
                    {!streamRef.current && !recordedBlob && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={startCamera}
                      >
                        <Camera className="w-4 h-4 mr-2" />
                        Activer caméra
                      </Button>
                    )}
                    
                    {streamRef.current && !isRecording && (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={startRecording}
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Enregistrer
                      </Button>
                    )}
                    
                    {isRecording && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={stopRecording}
                      >
                        <Square className="w-4 h-4 mr-2" />
                        Stop
                      </Button>
                    )}
                  </div>

                  {/* Statut vidéo (l'aperçu live est affiché à droite) */}
                  {(recordedBlob || currentDraft.video_url) && (
                    <p className="text-xs text-green-600">
                      ✓ Vidéo chargée — voir l'aperçu à droite
                    </p>
                  )}
                  {!recordedBlob && !currentDraft.video_url && streamRef.current && (
                    <p className="text-xs text-muted-foreground">
                      Aperçu caméra disponible à droite.
                    </p>
                  )}
                </div>

                <Separator />

                {/* Channels */}
                <div className="space-y-3">
                  <Label className="text-base font-semibold">Canaux de publication</Label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-muted">
                      <Checkbox
                        checked={currentDraft.channels?.instagram_reel || false}
                        onCheckedChange={(checked) => 
                          setCurrentDraft({
                            ...currentDraft,
                            channels: { ...currentDraft.channels!, instagram_reel: !!checked }
                          })
                        }
                      />
                      <Instagram className="w-4 h-4 text-pink-600" />
                      <span className="text-sm">Instagram Reel</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-muted">
                      <Checkbox
                        checked={currentDraft.channels?.facebook_reel || false}
                        onCheckedChange={(checked) => 
                          setCurrentDraft({
                            ...currentDraft,
                            channels: { ...currentDraft.channels!, facebook_reel: !!checked }
                          })
                        }
                      />
                      <Facebook className="w-4 h-4 text-blue-600" />
                      <span className="text-sm">Facebook Reel</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-muted">
                      <Checkbox
                        checked={currentDraft.channels?.linkedin_reel || false}
                        onCheckedChange={(checked) => 
                          setCurrentDraft({
                            ...currentDraft,
                            channels: { ...currentDraft.channels!, linkedin_reel: !!checked }
                          })
                        }
                      />
                      <Linkedin className="w-4 h-4 text-blue-700" />
                      <span className="text-sm">LinkedIn Reel</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-muted opacity-50">
                      <Checkbox
                        checked={currentDraft.channels?.tiktok || false}
                        onCheckedChange={(checked) => 
                          setCurrentDraft({
                            ...currentDraft,
                            channels: { ...currentDraft.channels!, tiktok: !!checked }
                          })
                        }
                      />
                      <span className="text-sm">TikTok (bientôt)</span>
                    </label>
                  </div>
                </div>

                <Separator />

                {/* Caption */}
                <div className="space-y-2">
                  <Label>Légende / Caption</Label>
                  <Textarea
                    value={currentDraft.caption || ''}
                    onChange={(e) => setCurrentDraft({ ...currentDraft, caption: e.target.value })}
                    rows={3}
                    placeholder="Texte accompagnant votre reel..."
                  />
                </div>

                {/* Hashtags */}
                <div className="space-y-2">
                  <Label>Hashtags</Label>
                  <Input
                    value={currentDraft.hashtags || ''}
                    onChange={(e) => setCurrentDraft({ ...currentDraft, hashtags: e.target.value })}
                    placeholder="#naturopathie #reels..."
                  />
                  <div className="flex flex-wrap gap-1 mt-2">
                    {NATUROPATHY_HASHTAGS.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        className="px-2 py-0.5 text-xs bg-muted hover:bg-primary/10 text-muted-foreground hover:text-primary rounded-full border transition-colors"
                        onClick={() => {
                          const current = currentDraft.hashtags || '';
                          if (!current.includes(tag)) {
                            setCurrentDraft({
                              ...currentDraft,
                              hashtags: current ? `${current} ${tag}` : tag,
                            });
                          }
                        }}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right: Live video preview + branding options */}
              <div className="space-y-4">
                {/* Live video preview (caméra ou vidéo importée) */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Aperçu vidéo</Label>
                  <div className="relative aspect-[9/16] w-full max-w-sm mx-auto bg-black rounded-lg overflow-hidden border">
                    <video
                      ref={videoPreviewRef}
                      className="w-full h-full object-cover"
                      playsInline
                      muted={!recordedBlob}
                    />
                    {!streamRef.current && !recordedBlob && !currentDraft.video_url && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground gap-2 bg-muted/80">
                        <Video className="w-12 h-12 opacity-40" />
                        <p className="text-xs text-center px-4">
                          Importez une vidéo ou activez la caméra pour voir l'aperçu ici.
                        </p>
                      </div>
                    )}
                    {isRecording && (
                      <div className="absolute top-2 left-2 flex items-center gap-1.5 px-2 py-1 rounded-full bg-red-600 text-white text-xs font-semibold">
                        <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                        REC
                      </div>
                    )}
                  </div>
                </div>

                {/* Master toggle for branding */}
                <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Palette className="w-5 h-5 text-primary" />
                      <Label htmlFor="apply-branding" className="text-base font-semibold cursor-pointer">
                        Appliquer le branding vidéo
                      </Label>
                    </div>
                    <Switch
                      id="apply-branding"
                      checked={currentDraft.branding?.apply_branding ?? false}
                      onCheckedChange={(checked) => setCurrentDraft({
                        ...currentDraft,
                        branding: {
                          ...(currentDraft.branding || DEFAULT_BRANDING),
                          apply_branding: checked,
                        }
                      })}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Si activé, le logo et le titre seront incrustés via Cloudinary avant l'envoi à Make.com
                  </p>
                </div>

                {/* Branding options (only shown if apply_branding is true) */}
                {currentDraft.branding?.apply_branding && (
                  <div className="p-4 bg-muted/50 rounded-lg space-y-6">
                    {/* Logo options */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="logo-visible" className="font-semibold">Logo</Label>
                        <Switch
                          id="logo-visible"
                          checked={currentDraft.branding?.logo?.visible ?? true}
                          onCheckedChange={(checked) => setCurrentDraft({
                            ...currentDraft,
                            branding: {
                              ...currentDraft.branding!,
                              logo: { ...currentDraft.branding!.logo, visible: checked }
                            }
                          })}
                        />
                      </div>

                      {currentDraft.branding?.logo?.visible && (
                        <>
                          {/* Logo position */}
                          <div className="space-y-2">
                            <Label>Position du logo</Label>
                            <Select
                              value={currentDraft.branding?.logo?.position || 'top_left'}
                              onValueChange={(value: any) => setCurrentDraft({
                                ...currentDraft,
                                branding: {
                                  ...currentDraft.branding!,
                                  logo: { ...currentDraft.branding!.logo, position: value }
                                }
                              })}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="top_left">Haut gauche</SelectItem>
                                <SelectItem value="top_right">Haut droit</SelectItem>
                                <SelectItem value="bottom_left">Bas gauche</SelectItem>
                                <SelectItem value="bottom_right">Bas droit</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Logo size */}
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Label>Taille du logo</Label>
                              <span className="text-xs text-muted-foreground">{currentDraft.branding?.logo?.size_px || 140}px</span>
                            </div>
                            <Slider
                              value={[currentDraft.branding?.logo?.size_px || 140]}
                              min={60}
                              max={300}
                              step={10}
                              onValueChange={([value]) => setCurrentDraft({
                                ...currentDraft,
                                branding: {
                                  ...currentDraft.branding!,
                                  logo: { ...currentDraft.branding!.logo, size_px: value }
                                }
                              })}
                            />
                          </div>

                          {/* Logo margin */}
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Label>Marge</Label>
                              <span className="text-xs text-muted-foreground">{currentDraft.branding?.logo?.margin_px || 40}px</span>
                            </div>
                            <Slider
                              value={[currentDraft.branding?.logo?.margin_px || 40]}
                              min={10}
                              max={100}
                              step={5}
                              onValueChange={([value]) => setCurrentDraft({
                                ...currentDraft,
                                branding: {
                                  ...currentDraft.branding!,
                                  logo: { ...currentDraft.branding!.logo, margin_px: value }
                                }
                              })}
                            />
                          </div>
                        </>
                      )}
                    </div>

                    <Separator />

                    {/* Title options */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="title-enabled" className="font-semibold">Titre visuel</Label>
                        <Switch
                          id="title-enabled"
                          checked={currentDraft.branding?.visual_title?.enabled ?? false}
                          onCheckedChange={(checked) => setCurrentDraft({
                            ...currentDraft,
                            branding: {
                              ...currentDraft.branding!,
                              visual_title: { ...currentDraft.branding!.visual_title, enabled: checked }
                            }
                          })}
                        />
                      </div>

                      {currentDraft.branding?.visual_title?.enabled && (
                        <>
                          {/* Title text */}
                          <div className="space-y-2">
                            <Label>Texte du titre</Label>
                            <Input
                              value={currentDraft.branding?.visual_title?.text || ''}
                              onChange={(e) => setCurrentDraft({
                                ...currentDraft,
                                branding: {
                                  ...currentDraft.branding!,
                                  visual_title: { ...currentDraft.branding!.visual_title, text: e.target.value }
                                }
                              })}
                              placeholder="Titre affiché sur la vidéo"
                              maxLength={60}
                            />
                          </div>

                          {/* Title position */}
                          <div className="space-y-2">
                            <Label>Position du titre</Label>
                            <Select
                              value={currentDraft.branding?.visual_title?.position || 'bottom_center'}
                              onValueChange={(value: any) => setCurrentDraft({
                                ...currentDraft,
                                branding: {
                                  ...currentDraft.branding!,
                                  visual_title: { ...currentDraft.branding!.visual_title, position: value }
                                }
                              })}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="top_left">Haut gauche</SelectItem>
                                <SelectItem value="top_center">Haut centré</SelectItem>
                                <SelectItem value="top_right">Haut droit</SelectItem>
                                <SelectItem value="bottom_left">Bas gauche</SelectItem>
                                <SelectItem value="bottom_center">Bas centré</SelectItem>
                                <SelectItem value="bottom_right">Bas droit</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Title size */}
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <Label>Taille du titre</Label>
                              <span className="text-xs text-muted-foreground">{currentDraft.branding?.visual_title?.size_px || 48}px</span>
                            </div>
                            <Slider
                              value={[currentDraft.branding?.visual_title?.size_px || 48]}
                              min={24}
                              max={72}
                              step={2}
                              onValueChange={([value]) => setCurrentDraft({
                                ...currentDraft,
                                branding: {
                                  ...currentDraft.branding!,
                                  visual_title: { ...currentDraft.branding!.visual_title, size_px: value }
                                }
                              })}
                            />
                          </div>

                          {/* Title font */}
                          <div className="space-y-2">
                            <Label>Police du titre</Label>
                            <Select
                              value={currentDraft.branding?.visual_title?.font || 'Montserrat'}
                              onValueChange={(value) => setCurrentDraft({
                                ...currentDraft,
                                branding: {
                                  ...currentDraft.branding!,
                                  visual_title: { ...currentDraft.branding!.visual_title, font: value }
                                }
                              })}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {AVAILABLE_FONTS.map((font) => (
                                  <SelectItem key={font.value} value={font.value}>
                                    <span style={{ fontFamily: font.value }}>{font.label}</span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Title color */}
                          <div className="space-y-2">
                            <Label>Couleur du titre</Label>
                            <Select
                              value={currentDraft.branding?.visual_title?.color || 'white'}
                              onValueChange={(value) => setCurrentDraft({
                                ...currentDraft,
                                branding: {
                                  ...currentDraft.branding!,
                                  visual_title: { ...currentDraft.branding!.visual_title, color: value }
                                }
                              })}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {AVAILABLE_COLORS.map((color) => (
                                  <SelectItem key={color.value} value={color.value}>
                                    <div className="flex items-center gap-2">
                                      <div 
                                        className="w-4 h-4 rounded border border-gray-300" 
                                        style={{ backgroundColor: color.hex }}
                                      />
                                      {color.label}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Info note */}
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-800">
                    <strong>Note:</strong> {currentDraft.branding?.apply_branding 
                      ? "Le branding sera appliqué via Cloudinary lors de la publication. La vidéo finale MP4 contiendra le logo et le titre incrustés."
                      : "Sans branding, la vidéo sera publiée telle quelle vers Make.com."}
                  </p>
                </div>

                {/* Visual branding preview */}
                {currentDraft.branding?.apply_branding && (
                  <div className="space-y-3">
                    <Label className="text-sm font-semibold">Aperçu du branding</Label>
                    <div 
                      className="relative w-full aspect-[9/16] bg-gradient-to-b from-gray-800 to-gray-900 rounded-lg overflow-hidden border-2 border-dashed border-muted"
                      style={{ maxHeight: '400px' }}
                    >
                      {/* Video placeholder background */}
                      <div className="absolute inset-0 flex items-center justify-center text-muted-foreground/50">
                        <Video className="w-16 h-16" />
                      </div>
                      
                      {/* Logo preview */}
                      {currentDraft.branding?.logo?.visible && (
                        <div 
                          className="absolute"
                          style={{
                            top: currentDraft.branding.logo.position.includes('top') 
                              ? `${(currentDraft.branding.logo.margin_px / 1920) * 100}%` 
                              : 'auto',
                            bottom: currentDraft.branding.logo.position.includes('bottom') 
                              ? `${(currentDraft.branding.logo.margin_px / 1920) * 100}%` 
                              : 'auto',
                            left: currentDraft.branding.logo.position.includes('left') 
                              ? `${(currentDraft.branding.logo.margin_px / 1080) * 100}%` 
                              : 'auto',
                            right: currentDraft.branding.logo.position.includes('right') 
                              ? `${(currentDraft.branding.logo.margin_px / 1080) * 100}%` 
                              : 'auto',
                          }}
                        >
                          <img 
                            src={logoIcon} 
                            alt="Logo" 
                            style={{
                              // Scale logo size relative to preview container (preview is ~225px wide for 400px height at 9:16)
                              // Map 60-300px range to visible preview sizes (20-100px)
                              width: `${Math.max(20, Math.min(100, (currentDraft.branding.logo.size_px / 300) * 100))}px`,
                              height: 'auto',
                            }}
                            className="object-contain drop-shadow-lg"
                          />
                        </div>
                      )}
                      
                      {/* Title preview */}
                      {currentDraft.branding?.visual_title?.enabled && currentDraft.branding.visual_title.text && (
                        <div 
                          className="absolute left-0 right-0 px-4 text-center"
                          style={{
                            top: currentDraft.branding.visual_title.position.includes('top') ? '5%' : 'auto',
                            bottom: currentDraft.branding.visual_title.position.includes('bottom') ? '5%' : 'auto',
                            textAlign: currentDraft.branding.visual_title.position.includes('left') 
                              ? 'left' 
                              : currentDraft.branding.visual_title.position.includes('right') 
                                ? 'right' 
                                : 'center',
                          }}
                        >
                          <span 
                            className="font-bold drop-shadow-lg"
                            style={{
                              fontFamily: currentDraft.branding.visual_title.font || 'Montserrat',
                              fontSize: `${Math.max(10, (currentDraft.branding.visual_title.size_px / 48) * 14)}px`,
                              color: AVAILABLE_COLORS.find(c => c.value === currentDraft.branding?.visual_title?.color)?.hex || '#FFFFFF',
                              textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                            }}
                          >
                            {currentDraft.branding.visual_title.text}
                          </span>
                        </div>
                      )}
                      
                      {/* Preview label */}
                      <div className="absolute bottom-2 left-2 right-2 text-center">
                        <span className="text-xs text-white/60 bg-black/40 px-2 py-1 rounded">
                          Aperçu (proportions 9:16)
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setEditModalOpen(false);
              stopCamera();
              setRecordedBlob(null);
            }}>
              Annuler
            </Button>
            <Button onClick={saveDraft} disabled={uploading}>
              {uploading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isCreating ? 'Créer le brouillon' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
